// ═══════════════════════════════════════════════════════════════════
//  MANUAL RECORDS (AB#868)
//
//  Staff recording an appointment the scheduling system would refuse:
//  one already past (a walk-in seen before it was booked), or one inside
//  the 8-hour minimum notice. Behind `amsStaffManualRecords`. Either way
//  the household is told nothing, and a past one has no Cal booking at
//  all: CMS answers it under a synthetic uid, `cms:<request id>`.
//
//  The two triggers differ on purpose (decision ams-9yyh). A past cell
//  opens the booking dialog in record mode straight away, because Cal
//  would refuse it anyway. A time inside the notice is tried the normal
//  way first, and "Book anyway, no notifications" is offered only once
//  the server has said no — and only when the start really is within 8
//  hours, since the same refusal code covers the 28-day window too.
//
//  Every judgement here is between instants, so it holds in any browser
//  zone: the slot's start is resolved against the site's zone by the
//  caller (`zonedTimeToInstant`), and "now" is the clock's.
// ═══════════════════════════════════════════════════════════════════

import type { BookingDetails } from "../bookingCalendarApi"

/** The server's cap on the reason: ManualRecordRequest.MaxReasonLength. */
export const MAX_MANUAL_RECORD_REASON_LENGTH = 500

/** Every site's minimum booking notice, the window "Book anyway, no notifications" is offered in. */
export const MINIMUM_NOTICE_MS = 8 * 60 * 60 * 1000

/** The metadata key the server marks a recorded booking with, always "yes" (BookingRecordReader). */
export const RECORDED_WITHOUT_SCHEDULING_KEY = "recordedWithoutScheduling"

/** The refusal codes the staff booking endpoint answers a manual record with. */
export const MANUAL_RECORD_CODES = {
  /** 422: Cal refused the time for the notice or the scheduling window. */
  outsideBookingWindow: "outsideBookingWindow",
  /** 422: Cal refused the time as already past. */
  startInThePast: "startInThePast",
  /** 422: Cal still refused the notice: the scheduling system cannot take the bypass yet. */
  bypassUnavailable: "minimumNoticeBypassUnavailable",
  /** 400: a manual record of a time more than 8 hours ahead, which is booked normally instead. */
  notApplicable: "manualRecordNotApplicable",
  /** 400: the flag is off on the server. */
  recordsOff: "manualRecordsOff",
  /** 400: the reason was blank or too long. */
  reasonRequired: "manualRecordReasonRequired",
} as const

/** Whether a reason would pass the server's check: something other than whitespace, within the cap. */
export function isManualRecordReasonValid(reason: string): boolean {
  const trimmed = reason.trim()
  return trimmed.length > 0 && trimmed.length <= MAX_MANUAL_RECORD_REASON_LENGTH
}

/** The reason as the request body takes it, or nothing when no manual record is being asked for. */
export function manualRecordFields(armed: boolean, reason: string): { manualRecordReason?: string } {
  return armed ? { manualRecordReason: reason.trim() } : {}
}

/** Whether a slot's start has already passed: what opens the dialog in record mode. */
export function hasStartPassed(start: Date, now: Date): boolean {
  return start.getTime() <= now.getTime()
}

/** Whether a slot's start is still ahead but inside the 8-hour minimum notice. */
export function isInsideMinimumNotice(start: Date, now: Date): boolean {
  const ahead = start.getTime() - now.getTime()
  return ahead > 0 && ahead <= MINIMUM_NOTICE_MS
}

/** Whether the new-booking dialog opens on this start in record mode: the flag is on and it has passed. */
export function opensInRecordMode(enabled: boolean, start: Date, now: Date): boolean {
  return enabled && hasStartPassed(start, now)
}

/** The code a failed request carries, with its status, or nothing. */
function refusal(error: unknown): { status?: number | string; code?: unknown } {
  const failure = error as { status?: number | string; data?: unknown } | undefined
  return { status: failure?.status, code: (failure?.data as { code?: unknown } | undefined)?.code }
}

/**
 * What the dialog does with a refusal of an ordinary staff booking, given the flag and the clock:
 *
 * - `record`: Cal said the start is past — switch to record mode (the flag is on).
 * - `offerNotice`: Cal refused the notice and the start is within 8 hours — offer "Book anyway, no
 *   notifications" (the flag is on). A start further out was refused for the 28-day window, which no
 *   manual record gets past.
 * - `message`: a manual-record refusal with nothing to retry — show the server's words.
 * - `none`: not a manual-record matter; the dialog's other handling applies.
 */
export type ManualRecordReaction = "record" | "offerNotice" | "message" | "none"

export function manualRecordReaction(error: unknown, enabled: boolean, start: Date, now: Date): ManualRecordReaction {
  const { status, code } = refusal(error)

  if (status === 422 && code === MANUAL_RECORD_CODES.bypassUnavailable) return "message"
  if (status === 400 && (code === MANUAL_RECORD_CODES.notApplicable
    || code === MANUAL_RECORD_CODES.recordsOff
    || code === MANUAL_RECORD_CODES.reasonRequired)) return "message"

  if (!enabled || status !== 422) return "none"

  if (code === MANUAL_RECORD_CODES.startInThePast) return "record"

  if (code === MANUAL_RECORD_CODES.outsideBookingWindow) {
    // The clock may have moved past the start while the form was open.
    if (hasStartPassed(start, now)) return "record"
    if (isInsideMinimumNotice(start, now)) return "offerNotice"
  }

  return "none"
}

/**
 * Whether an appointment was recorded without scheduling: no Cal booking behind it, so nothing that
 * acts through Cal (cancel, reassign, reschedule, change type) can be done to it. From the summary's
 * own flag, or the metadata key for a server that sends only that.
 */
export function isRecordedWithoutScheduling(booking: Pick<BookingDetails, "recordedWithoutScheduling" | "metadata"> | undefined): boolean {
  if (!booking) return false
  return booking.recordedWithoutScheduling === true
    || booking.metadata?.[RECORDED_WITHOUT_SCHEDULING_KEY]?.trim().toLowerCase() === "yes"
}

/**
 * The uid a worklist row opens the appointment dialog by: the server's `openUid` (the Cal uid, or the
 * synthetic one for a recorded row), falling back to the Cal uid for a server that predates it. Null
 * when there is nothing to open.
 */
export function openableUid(row: { openUid?: string | null; calBookingUid?: string | null }): string | null {
  return row.openUid?.trim() || row.calBookingUid?.trim() || null
}
